// Unpublished Work © 2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {useLocation, useParams, withRouter} from 'react-router-dom';
import WorkboardFormStepper from './workboard-form-stepper';
import {Box} from '@deere/isg.component-library';
import {
    EXISTING_WORKBOARD,
    EXISTING_WORKBOARD_TEMPLATE,
    NEW_WORKBOARD,
    NEW_WORKBOARD_FROM_TEMPLATE,
    NEW_WORKBOARD_TEMPLATE,
    USER
} from '../constants/workboard-creation-flows';
import SetupWorkboard from './setup/setup-workboard';
import AssignWorkboard from './assign/assign-workboard';
import {PRO_GOLF_LABOR_RESKIN} from 'Common/constants/isg-toggles';
import {WORKBOARD} from 'Common/constants/routes';
import {getWorkboardType} from '../utils/workboard-initialize';
import {getWorkboardTemplateWithDetails, getWorkboardV2} from 'Services/work-board-service';
import {fetchEffectDataV2} from 'Src/ui-core/utils/react-utils';
import {JOB} from 'OnLabor/workboard/constants/workboard-creation-flows';
import {setInitalWorkboardData} from 'Src/ui-core/store/actions/setup-workboard';
import {fetchAndFilterInactiveOperators} from '../utils/fetch-operators';
import {getJobTemplates} from 'Services/job-service';
import {alphaNumericCompare} from 'Src/ui-core/models/maintenance';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import moment from 'moment';
import {MANUAL_DATA_TIME_FORMAT} from 'Src/ui-core/utils/time-utils';
import {getWorkboardDateTimeForDetails} from '../utils/workboard-data-utils';

function getWorkItemsFromJobs(jobs) {
    const workItems = [];

    jobs.forEach((job) => {
        workItems.push(...job.workItems || []);
    });
    return workItems;
}

function addJobTemplateIdToWorkItems(workboardData) {
    if (!workboardData) {
        return workboardData;
    }

    const jobs = workboardData.jobs ?? [];
    const workItems = getWorkItemsFromJobs(jobs);

    const jobIdToTemplateId = jobs.reduce((acc, job) => {
        if (job && job.jobId) {
            acc[job.jobId] = job.jobTemplateId ?? null;
        }
        return acc;
    }, {});

    const updatedWorkItems = workItems.map((wi) => {
        const jobTemplateIdFromJob = jobIdToTemplateId[wi?.jobId];

        return {
            ...wi,
            jobTemplateId: wi?.jobTemplateId ?? jobTemplateIdFromJob ?? null
        };
    });

    return {
        ...workboardData,
        workItems: updatedWorkItems
    };
}

function parseRoute(route) {
    const obj = JSON.parse(route);

    return {
        routeCustomDefn: obj.routeCustomDefn ?? [],
        route: obj.selectedRoute?.replace(/"/g, '') ?? ''
    };
}

function addRouteToWorkItems(workboardData) {
    const workItems = workboardData.workItems.map((wi) => ({
        ...wi,
        ...parseRoute(wi.route)
    }));

    return {
        ...workboardData,
        workItems
    };
}

function getRowOrderForTemplate(workboardData) {
    const isUserFlow = workboardData.creationFlow === USER;

    if (isUserFlow && workboardData.selectedOperators) {
        return [...workboardData.selectedOperators]
            .sort((a, b) => a.seq - b.seq)
            .map((item) => item.operatorId);
    }
    if (!isUserFlow && workboardData.selectedJobs) {
        return [...workboardData.selectedJobs]
            .sort((a, b) => a.seq - b.seq)
            .map((job) => job.jobId);
    }
    return [];
}

function getRowOrder(workboardData) {
    const isUserFlow = workboardData.creationFlow === USER;

    if (isUserFlow && workboardData.userOrder) {
        return [
            ...new Set(
                [...workboardData.userOrder]
                    .sort((a, b) => a.seq - b.seq)
                    .map((item) => item.userId)
            )
        ];
    }
    if (!isUserFlow && workboardData.jobs) {
        return [...workboardData.jobs]
            .sort((a, b) => a.seq - b.seq)
            .map((job) => job.jobTemplateId);
    }
    return [];
}

function getIdForQuery(routeId, search) {
    const params = new URLSearchParams(search);
    const fromTemplate = params.get('fromTemplate');

    return fromTemplate ?? routeId;
}

function getOperatorFetchOptions() {
    return {
        operatorDateFilter: (date) => date,
        tagOnTimeOff: true
    };
}

function parseResponse(type, response) {
    if (type === EXISTING_WORKBOARD) {
        return addJobTemplateIdToWorkItems(response);
    }

    return addRouteToWorkItems(response);
}

function initializeState(membership) {
    const [activeStep, setActiveStep] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [workboardCreationFlow, setWorkboardCreationFlow] = React.useState(membership.properties.workboard_creation_flow || JOB);

    return {
        activeStep,
        setActiveStep,
        loading,
        setLoading,
        error,
        setError,
        workboardCreationFlow,
        setWorkboardCreationFlow
    };
}

function getTimeFromTemplate(startTime) {
    const base = moment(startTime, 'YYYY-MM-DD HH:mm:ss');

    return moment().set({
        hour: base.hour(),
        minute: base.minute(),
        second: base.second(),
        millisecond: 0
    }).format('YYYY-MM-DD HH:mm:ss');
}

function WorkboardContainer(props) {
    const {
        translations, history, isgToggles, membership, setInitalWorkboardData, orgId
    } = props;

    const {addToast} = useToast();

    const {
        pathname,
        search,
        state: locationState
    } = useLocation();
    const {id: routeId} = useParams();

    const id = getIdForQuery(routeId, search);

    const featureEnabled = Boolean(isgToggles?.[PRO_GOLF_LABOR_RESKIN]);
    const {
        operatorDateFilter, tagOnTimeOff
    } = getOperatorFetchOptions();

    // TODO remove after toggle release
    React.useEffect(() => {
        if (!featureEnabled) {
            history.replace(WORKBOARD);
        }
    }, [featureEnabled, history]);

    if (!featureEnabled) {
        return null;
    }

    const type = React.useMemo(
        () => getWorkboardType(routeId, {
            pathname,
            search
        }),
        [routeId, pathname, search]
    );

    const shouldPreserveCopyState = type === NEW_WORKBOARD && Boolean(locationState?.preserveWorkboardCopy);

    const {
        activeStep,
        setActiveStep,
        loading,
        setLoading,
        error,
        setError,
        workboardCreationFlow,
        setWorkboardCreationFlow
    } = initializeState(membership);

    const SETTINGS_BY_TYPE = {
        [NEW_WORKBOARD]: {
            title: translations.ONLINK_ADD_WORKBOARD,
            isEditing: false
        },
        [NEW_WORKBOARD_FROM_TEMPLATE]: {
            title: translations.ONLINK_ADD_WORKBOARD,
            operatorIdAssesor: 'operatorId',
            workItemIdAssesor: 'workboardTemplateWorkItemId',
            fetcher: getWorkboardTemplateWithDetails,
            isEditing: false
        },
        [EXISTING_WORKBOARD]: {
            title: translations.ONLINK_EDIT_WORKBOARD,
            fetcher: getWorkboardV2,
            isEditing: true,
            operatorIdAssesor: 'appUserId',
            workItemIdAssesor: 'workItemId'
        },
        [NEW_WORKBOARD_TEMPLATE]: {
            title: translations.ONLINK_ADD_TEMPLATE,
            isEditing: false
        },
        [EXISTING_WORKBOARD_TEMPLATE]: {
            title: translations.ONLINK_EDIT_TEMPLATE,
            fetcher: getWorkboardTemplateWithDetails,
            isEditing: true,
            operatorIdAssesor: 'operatorId',
            workItemIdAssesor: 'workboardTemplateWorkItemId'
        }
    };

    React.useEffect(() => {
        if (shouldPreserveCopyState) {
            setError(null);
            setLoading(false);
            return undefined;
        }

        setLoading(true);
        setError(null);

        const errorToast = {
            message: translations.REFRESH_BROWSER_AND_TRY_AGAIN,
            title: translations.ERROR_OCCURRED_TITLE,
            persist: false,
            type: TOAST_TYPE.ERROR
        };

        const cleanup = fetchEffectDataV2(
            async ({signal}) => {
                const {templates} = await getJobTemplates(signal);

                const formattedJobs = templates.filter((job) => !job.inactive).map((jobTemplate) => {
                    return {
                        ...jobTemplate,
                        title: jobTemplate.title ?? '',
                        subtitle: `${translations.ONLINK_OPERATOR_COUNT}: ${jobTemplate.operatorCount ?? '---'}`,
                        id: jobTemplate.jobTemplateId
                    };
                })
                    .sort((a, b) => {
                        const fav = (b.isFavorite ? 1 : 0) - (a.isFavorite ? 1 : 0);

                        if (fav !== 0) {
                            return fav;
                        }

                        return alphaNumericCompare(a.title, b.title);
                    });

                const settings = SETTINGS_BY_TYPE[type];

                const hasFetcher = typeof settings.fetcher === 'function';

                if (hasFetcher) {
                    const response = await settings.fetcher(id, signal);

                    if (response?.success === false) {
                        addToast(errorToast);
                    } else {
                        const workboardData = parseResponse(type, response);
                        const date = type === NEW_WORKBOARD_FROM_TEMPLATE ?
                            getTimeFromTemplate(workboardData.startTime) :
                            getWorkboardDateTimeForDetails(workboardData);
                        const operators = await fetchAndFilterInactiveOperators(
                            orgId, operatorDateFilter(date), signal, tagOnTimeOff);

                        setWorkboardCreationFlow((prev) => workboardData?.creationFlow ?? prev);

                        setInitalWorkboardData({
                            initialData: {
                                ...workboardData,
                                date,
                                workboardId: workboardData.workboardId ?? (settings.isEditing ? id : null),
                                rowOrder: type === EXISTING_WORKBOARD_TEMPLATE ? getRowOrderForTemplate(workboardData) : getRowOrder(workboardData),
                                teamMembers: operators,
                                jobs: formattedJobs,
                                isEditing: Boolean(settings.isEditing),
                                creationFlow: workboardData?.creationFlow ?? workboardCreationFlow
                            },
                            fetchedWorkboard: workboardData,
                            operatorIdAssesor: settings.operatorIdAssesor,
                            workItemIdAssesor: settings.workItemIdAssesor
                        });
                    }
                } else {
                    const date = membership.properties.workboard_default_start_time ?
                        moment().startOf('day').set('hour', membership.properties.workboard_default_start_time) :
                        moment();
                    const operators = await fetchAndFilterInactiveOperators(
                        orgId, operatorDateFilter(date), signal, tagOnTimeOff);

                    setInitalWorkboardData({
                        initialData: {
                            date: date.format(MANUAL_DATA_TIME_FORMAT),
                            teamMembers: operators,
                            jobs: formattedJobs,
                            isEditing: Boolean(settings.isEditing),
                            creationFlow: ''
                        }
                    });
                }
            },
            {
                onError: (e) => {
                    setError(e);
                    addToast(errorToast);
                },
                onFinally: () => {
                    setLoading(false);
                }
            }
        );

        return cleanup;
    }, [type, id, search, shouldPreserveCopyState]);

    return (
        <WorkboardFormStepper
            activeStep={activeStep}
            error={error} // TODO global error handler
            groupByUser={workboardCreationFlow === USER}
            loading={loading} // TODO global loader
            setActiveStep={setActiveStep}
            title={SETTINGS_BY_TYPE[type].title}
            translations={translations}
            type={type}
            {...props}
        >
            <Box
                flexGrow={1}
                sx={{
                    overflowY: 'auto'
                }}
            >
                {activeStep === 0 ? (
                    <SetupWorkboard
                        groupBy={workboardCreationFlow}
                        loading={loading} // TODO Move this to global context
                        translations={translations}
                        type={type}
                    />
                ) : (
                    <AssignWorkboard
                        groupBy={workboardCreationFlow}
                        translations={translations}
                    />
                )}
            </Box>
        </WorkboardFormStepper>
    );
}

WorkboardContainer.propTypes = {
    history: PropTypes.history,
    isgToggles: PropTypes.object,
    membership: PropTypes.membership,
    orgId: PropTypes.number,
    setInitalWorkboardData: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        translations: state.translations,
        isgToggles: state.account.isgToggles,
        membership: state.membership,
        orgId: state.membership.orgId
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        setInitalWorkboardData(value) {
            dispatch(setInitalWorkboardData(value));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(WorkboardContainer));
